/**
 * Game state reducer following Redux patterns
 */

import type { GameState, GameAction } from './types.ts';
import {
  createEmptyBoard,
  makeMove,
  checkWinner,
  isBoardFull,
  getOpponent,
  isValidMove,
} from './utils.ts';

/**
 * Initial game state
 */
export const initialGameState: GameState = {
  board: createEmptyBoard(),
  currentPlayer: 'X',
  localPlayer: 'X',
  status: 'waiting',
  winner: null,
  isMyTurn: false,
};

/**
 * Game reducer function
 */
export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'START_GAME': {
      const { localPlayer } = action.payload;
      return {
        ...state,
        board: createEmptyBoard(),
        currentPlayer: 'X',
        localPlayer,
        status: 'playing',
        winner: null,
        isMyTurn: localPlayer === 'X',
      };
    }

    case 'MAKE_MOVE': {
      const { position } = action.payload;

      if (!state.isMyTurn || state.status !== 'playing') {
        return state;
      }

      if (!isValidMove(state.board, position)) {
        return state;
      }

      const newBoard = makeMove(state.board, position, state.localPlayer);
      const winner = checkWinner(newBoard);
      const isDraw = !winner && isBoardFull(newBoard);

      return {
        ...state,
        board: newBoard,
        currentPlayer: getOpponent(state.currentPlayer),
        status: winner ? 'won' : isDraw ? 'draw' : 'playing',
        winner,
        isMyTurn: false,
      };
    }

    case 'RECEIVE_MOVE': {
      const { position, player } = action.payload;

      if (state.status !== 'playing') {
        return state;
      }

      if (!isValidMove(state.board, position)) {
        return state;
      }

      const newBoard = makeMove(state.board, position, player);
      const winner = checkWinner(newBoard);
      const isDraw = !winner && isBoardFull(newBoard);

      return {
        ...state,
        board: newBoard,
        currentPlayer: getOpponent(state.currentPlayer),
        status: winner ? 'won' : isDraw ? 'draw' : 'playing',
        winner,
        isMyTurn: !isDraw && !winner,
      };
    }

    case 'RESET_GAME': {
      return {
        ...initialGameState,
        localPlayer: state.localPlayer,
        status: 'playing',
        isMyTurn: state.localPlayer === 'X',
      };
    }

    case 'SET_WAITING': {
      return {
        ...state,
        status: 'waiting',
        isMyTurn: false,
      };
    }

    default:
      return state;
  }
}
